/**
 * Frontend-only piece grouping utilities
 * Groups identical pieces for better UI display without changing database
 */

/**
 * Generate a unique grouping key for pieces that should be displayed together
 * Pieces minted in the same batch will have matching keys
 * 
 * @param {Object} piece - Piece object
 * @returns {string} - Grouping key
 */
export function getPieceGroupKey(piece) {
  // Group pieces that are actual duplicates (editions from same creation)
  // Include enough unique identifiers to distinguish separate pieces in capsules
  // while still grouping true editions together
  const parts = [
    piece.creator_wallet || piece.creator_id || '',
    piece.name || '',
    piece.description || '',
    piece.category || '',
    piece.color || '',
    piece.thumbnail_url || '',
    piece.model_url || piece.preview_glb_url || '',
    piece.price || '',
    // Include creation timestamp to separate pieces created at different times
    // Editions are created in rapid succession, capsule pieces have different timestamps
    Math.floor(new Date(piece.created_at || 0).getTime() / 60000) // Round to nearest minute
  ];
  
  return parts.join('|');
}

/**
 * Group pieces by their grouping key
 * Returns both grouped and ungrouped pieces
 * 
 * @param {Array} pieces - Array of piece objects
 * @returns {Object} - { groups: Map, ungroupedPieces: Array }
 */
export function groupPieces(pieces) {
  if (!pieces || !Array.isArray(pieces)) {
    return { groups: new Map(), ungroupedPieces: [] };
  }

  const groupMap = new Map();
  
  pieces.forEach(piece => {
    const groupKey = getPieceGroupKey(piece);
    
    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }
    
    groupMap.get(groupKey).push(piece);
  });
  
  return { groups: groupMap, ungroupedPieces: [] };
}

/**
 * Convert grouped pieces into display-ready format
 * Each group becomes a single card with multiple pieces inside
 * 
 * @param {Array} pieces - Array of piece objects
 * @returns {Array} - Array of grouped piece objects for display
 */
export function createGroupedPieceCards(pieces) {
  if (!pieces || !Array.isArray(pieces)) {
    return [];
  }

  const { groups } = groupPieces(pieces);
  const groupedCards = [];
  
  groups.forEach((piecesInGroup, groupKey) => {
    // Sort by created date (newest first - this matches the piece selector order)
    const sortedPieces = [...piecesInGroup].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    const firstPiece = sortedPieces[0]; // Use the newest piece as representative
    const isGroup = sortedPieces.length > 1;
    
    // Create a display card
    // For groups, we use the first piece as the "representative"
    // but add metadata about the group
    const displayCard = {
      ...firstPiece, // Use first piece as base
      
      // Add grouping metadata
      _isGroup: isGroup,
      _groupKey: groupKey,
      _groupSize: sortedPieces.length,
      _groupPieces: sortedPieces, // All individual pieces in mint order
      _availableCount: sortedPieces.filter(p => 
        p.listing_status === 'listed' || 
        (p.is_public && !p.is_draft && p.mint_address)
      ).length,
      
      // For UI display
      displayName: firstPiece.name,
      displayPrice: firstPiece.price || firstPiece.listing_price,
      
      // Keep original ID accessible
      _firstPieceId: firstPiece.id,
      _representativeId: firstPiece.id
    };
    
    groupedCards.push(displayCard);
  });
  
  // Sort grouped cards by most recent creation
  groupedCards.sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });
  
  return groupedCards;
}

/**
 * Get individual pieces from a grouped card
 * @param {Object} groupedCard - Grouped card object
 * @returns {Array} - Array of individual pieces
 */
export function getIndividualPieces(groupedCard) {
  if (!groupedCard || !groupedCard._isGroup) {
    return [groupedCard]; // Return as single-item array
  }
  
  return groupedCard._groupPieces || [];
}

/**
 * Check if a card represents a group
 * @param {Object} card - Card object
 * @returns {boolean}
 */
export function isGroupedCard(card) {
  return card && card._isGroup === true;
}

/**
 * Get display label for group size
 * @param {Object} card - Card object
 * @returns {string} - Display label like "3 available"
 */
export function getGroupLabel(card) {
  if (!isGroupedCard(card)) {
    return null;
  }
  
  const total = card._groupSize || 0;
  return `${total} pieces`;
}

/**
 * Get an available piece from a group for purchase
 * @param {Object} groupedCard - Grouped card object
 * @returns {Object|null} - First available piece or null
 */
export function getAvailablePieceFromGroup(groupedCard) {
  if (!isGroupedCard(groupedCard)) {
    // Single piece - return if available
    if (groupedCard.listing_status === 'listed' || 
        (groupedCard.is_public && !groupedCard.is_draft && groupedCard.mint_address)) {
      return groupedCard;
    }
    return null;
  }
  
  // Find first available piece in group
  const pieces = getIndividualPieces(groupedCard);
  return pieces.find(piece => 
    piece.listing_status === 'listed' || 
    (piece.is_public && !piece.is_draft && piece.mint_address)
  ) || null;
}

export default {
  getPieceGroupKey,
  groupPieces,
  createGroupedPieceCards,
  getIndividualPieces,
  isGroupedCard,
  getGroupLabel,
  getAvailablePieceFromGroup
};

